<script lang="ts" setup>
import {requiredRule} from '../../Vee/rules/required.rule'
import {useValidator} from '../../Vee/useValidator'
import {Validator} from '../../Vee/validator'
import UButton from 'nvd-u/components/UButton.vue'
import WaitingAnimation from '../animated/WaitingAnimation.vue'
import {useToggle} from '../../composables/useToggle'
import {useRouter} from 'vue-router'
import {useSchemaStore} from "../../stores/schema.store";
import { useTabsStore } from "@/stores/tabs.store";
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

const schema = useSchemaStore()
const tabs = useTabsStore()
const router = useRouter()
const { t } = useI18n()

const aiExamples = computed(() => [
    {
        label: 'Book Store',
        desc: t('generate_ai.online_store_selling_books'),
        prompt: t('generate_ai.book_store_prompt')
    },
    {
        label: 'Trucking Business',
        desc: t('generate_ai.trucking_business_database'),
        prompt: t('generate_ai.trucking_business_prompt')
    },
    {
        label: 'Library Management System',
        desc: t('generate_ai.digital_library_management'),
        prompt: t('generate_ai.library_management_prompt')
    },
])
const {toggle, selected} = useToggle()

const v = useValidator(schema.aiForm, (v: Validator) => {
    v.addRule(requiredRule('prompt'))
})

async function save() {
    v.validate()
    if (v.hasErrors) return
    schema.createSchemaUsingAi()
}

interface GenerateWithAIEvent {
    failed: boolean
    message: string
    name: string
}

function getTitle() {
    let str = schema.aiForm.prompt.slice(0, 25)
    let lastSpaceIdx = str.indexOf(' ', -1)
    return str.slice(0, lastSpaceIdx)
}

function trimJson(json) {
    return json.slice(json.indexOf('{'), json.lastIndexOf('}') + 1)
}
</script>

<template>
    <form class="gen-with-ai" @submit.prevent="save">
        <div v-if="!schema.aiForm.loading">
            <p class="text-muted">{{ t('generate_ai.creates_project') }}</p>
            <div class="ai-generation-options">
                <label>{{ t('generate_ai.database') }}<select v-model="schema.aiForm.db"><option v-for="db in tabs.dbTypes" :key="db" :value="db">{{ db }}</option></select></label>
                <label>{{ t('generate_ai.scope') }}<select v-model="schema.aiForm.scope"><option value="focused">{{ t('generate_ai.focused') }}</option><option value="expanded">{{ t('generate_ai.expanded') }}</option></select></label>
                <label>{{ t('generate_ai.naming') }}<select v-model="schema.aiForm.naming"><option value="snake_case">snake_case</option><option value="camelCase">camelCase</option></select></label>
            </div>
            <div class="u-input-container u-textarea ai-prompt">
                <div class="u-form-group">
                    <textarea 
                        class="u-input" 
                        :value="schema.aiForm.prompt"
                        @input="schema.aiForm.prompt = $event.target.value"
                        :placeholder="t('generate_ai.whats_schema_about')"
                        :disabled="schema.aiForm.loading"
                        rows="3"
                    ></textarea>
                    <label>{{ t('generate_ai.whats_schema_about') }}</label>
                </div>
                <div v-if="v.errors.prompt && v.errors.prompt.length > 0" class="error-message">{{ Array.isArray(v.errors.prompt) ? v.errors.prompt[0] : v.errors.prompt }}</div>
            </div>
            <div class="text-danger text-small" v-if="schema.aiForm.error">{{ schema.aiForm.error }}</div>
            <h4>{{ $t('modals.examples') }}</h4>
            <div class="mb-3 mx-0 ai-example card"
                 v-for="example in aiExamples"
                 :key="example.label">
                <button type="button" class="header p-3 align-items-center ai-example__header" :aria-expanded="selected === example.label"
                     :class="{
                         'border-b1': selected === example.label,
                         'ai-example__header--selected': selected === example.label,
                         'has-hover': selected !== example.label
                     }"
                     @click="toggle(example.label)">
                    {{ example.label }}
                </button>
                <div class="body" :class="{'expanded': selected === example.label}">
                    <div class="d-flex justify-content-between align-items-center p-3 pb-0">
                        <small class="text-muted">{{ t('generate_ai.ai_prompt') }}</small>
                        <button type="button" v-if="selected === example.label" @click="schema.aiForm.prompt = example.prompt; v.validateField('prompt')">{{ t('generate_ai.use_example') }}</button>
                    </div>
                    <pre class="p-3 w100">{{ example.desc }}</pre>
                </div>
            </div>
        </div>
        <div v-else>
            <WaitingAnimation class="mb-4"/>
            <div class="text-muted text-center">
                {{ $t('generate_ai.generating_schema_redirect') }}
            </div>
        </div>
        <UButton class="mt-5" @click="save" :loading="schema.aiForm.loading" v-if="!schema.aiForm.loading">
            {{ t('generate_ai.generate') }}
        </UButton>
    </form>
</template>

<style lang="scss">
.ai-generation-options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.6rem; margin-bottom: 0.8rem; }
.ai-generation-options label { display: grid; gap: 0.25rem; }
.ai-generation-options select { width: 100%; padding: 0.5rem; border: 1px solid var(--border-color); border-radius: 0.4rem; background: var(--bg); color: var(--main-text-color); }
.gen-with-ai {
    .u-input-container.u-textarea .u-form-group .u-input {
        min-height: 8em;
        line-height: 1.2;
    }
    
    .error-message {
        color: var(--danger-color, #dc3545);
        font-size: 0.875rem;
        margin-top: 0.25rem;
    }

    .ai-example {
        border-radius: var(--border-radius);
        overflow: hidden;
        border: 1px solid var(--border-color);

        .header {
            cursor: pointer;
            width: 100%;
            text-align: left;
            font: inherit;
            border: 0;
            background: var(--bg);
            color: var(--main-text-color);
        }

        .ai-example__header--selected {
            background: var(--body-bg);
            color: var(--main-text-color);
        }

        .body {
            position: relative;
            padding: 0;
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.25s ease-in-out;

            &.expanded {
                max-height: 20em;
                transition: max-height 0.25s ease-in-out;
            }

            pre {
                margin-bottom: 0;
                padding: 1em;
                background-color: var(--bg);
                white-space: pre-wrap;
                word-wrap: break-word;
            }

            .copy-btn {
                position: absolute;
                right: 0.5em;
                top: 0.5em;
                z-index: 10;
            }
        }
    }
}
</style>
